import * as Sentry from '@sentry/vue'
import { setObjectToIndexedDB, Stores } from '@/utils/indexedDB/indexed.db'
import { DefaultCanvasBlocks, _migrateDefaultBlockIds, sanitizeBlocks } from '@/utils/CanvasBlocksLayout'
import SlideApi from '@/api/slide'
import Socket from '@/socket.js'

export default {
  // NOTES: This file's used for Content slides + import PDF/PPT
  async loadContentSlidesCanvasBlocks(editor, res) {
    const contentSlides = res.body.slides.filter((item) => {
      return item && item.type === 'freestyle'
    })
    const getContentPromises = this._getContentCanvasBlocks(editor,
      contentSlides)

    await Promise.all(getContentPromises).then((data) => {
      this.updateCanvasBlocksArrayToAllSlidesInPresentation(data, editor)
    })

    await this.updateCanvasBlockForSlideActive(editor, contentSlides)
  },

  _getContentCanvasBlocks(editor, contentSlides) {
    return contentSlides.map(async (s) => {
      if (s.canvasBlocksUrl) {
        try {
          const response = await fetch(s.canvasBlocksUrl).then((res) => res.json()).then((body) => ({ body }))

          const {
            canvasBlocksWithNewSignedUrls,
            hasExpiredImageBlock,
          } = await this._reSignedImages(response.body)

          await this._updateCanvasBlocksAfterReSignedImages(
            canvasBlocksWithNewSignedUrls,
            hasExpiredImageBlock,
            s.id,
            editor.$store.state.presentation.uniqueAccessCode,
          )

          let canvasBlocks = canvasBlocksWithNewSignedUrls || DefaultCanvasBlocks(s.id)
          canvasBlocks = _migrateDefaultBlockIds(canvasBlocks, s.id)

          setObjectToIndexedDB(s.id, Stores.contentSlideCanvasBlocksStore, {
            canvasBlocks,
          })

          return {
            id: s.id,
            canvasBlocks,
          }
        } catch (error) {
          // console.log(error)
        }
      }

      let canvasBlocks = DefaultCanvasBlocks(s.id)
      canvasBlocks = _migrateDefaultBlockIds(canvasBlocks, s.id)
      return {
        id: s.id,
        canvasBlocks,
      }
    })
  },

  updateCanvasBlocksArrayToAllSlidesInPresentation(data, editor) {
    // Update canvasBlocksArray to all slides data in the presentation
    editor.$store.state.presentation.slides = editor.$store.state.presentation.slides.map((s) => {
      if (s.type !== 'freestyle') return s
      const slideIndex = data.findIndex((t) => t.id === s.id)
      const dataCanvasBlocks = data[slideIndex] ? data[slideIndex].canvasBlocks : null

      return {
        ...s,
        canvasBlocks: dataCanvasBlocks,
      }
    })
  },

  async updateCanvasBlockForSlideActive(editor, contentSlides) {
    const contentSlideActiveIndex =
      contentSlides.findIndex((item) => item.id === editor.slideActive.id)
    if (contentSlideActiveIndex > -1) {
      try {
        const resGetContentSlideActive =
            await fetch(contentSlides[contentSlideActiveIndex].canvasBlocksUrl)
              .then((res) => res.json())
              .then((body) => ({ body }))
        let dataCanvasBlocks = resGetContentSlideActive.body ?
          resGetContentSlideActive.body :
          DefaultCanvasBlocks(editor.slideActive.id)

        dataCanvasBlocks = _migrateDefaultBlockIds(dataCanvasBlocks, editor.slideActive.id)

        editor.$store.dispatch('updateSlideActive', {
          canvasBlocks: dataCanvasBlocks,
        })
      } catch (e) {
        Sentry.captureException(e)
      }
    }
  },

  async _reSignedImages(blocks) {
    if (!blocks) return null

    const expiredImageBlocks = {}
    const canvasBlocksWithNewSignedUrls = {
      ...blocks,
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, block] of Object.entries(blocks)) {
      if (block.type === 'Image' && block.content && block.content.src) {
        const matched = block.content.src.match(/Expires=\d+&/) // 'Expires=1688614647&'
        const currentTime = new Date().getTime()
        let expires

        if (matched && matched.length) {
          expires = matched[0].substring(8, matched[0].length - 1)

          expires = new Date(parseInt(expires, 10) * 1000).getTime()

          if (expires <= currentTime) {
            expiredImageBlocks[`${key}`] = block.content.src
          }
        }
      }
    }

    if (Object.keys(expiredImageBlocks).length) {
      try {
        const arrLinkKey = Object.keys(expiredImageBlocks)
        const arrayLinkValue = Object.values(expiredImageBlocks)

        const response = await SlideApi.reSignedImages({
          arrLinks: arrayLinkValue,
        })

        if (response && response.length) {
          arrLinkKey.forEach((itemKey, index) => {
            canvasBlocksWithNewSignedUrls[`${itemKey}`].content.src = response[index]
          })
        }
      } catch (error) {
        // console.log(error)
      }
    }

    sanitizeBlocks(canvasBlocksWithNewSignedUrls)

    return {
      canvasBlocksWithNewSignedUrls,
      hasExpiredImageBlock: !!Object.keys(expiredImageBlocks).length,
    }
  },

  async _updateCanvasBlocksAfterReSignedImages(
    blocks,
    hasExpiredImageBlock,
    slideId,
    uniqueAccessCode,
  ) {
    if (hasExpiredImageBlock) {
      try {
        const payload = {
          canvasBlocks: blocks,
          id: slideId,
          type: 'freestyle',
          uniqueAccessCode,
          socketId: Socket.getSocketId(),
        }

        await SlideApi.updateSlideById(slideId, payload)
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  },
}
